// SidebarContext — content slot + collapse state for the AppShell sidebar.
//
// Two layers of context split internally:
//   - SidebarStateContext   — sidebarContent + isCollapsed. Re-renders
//     consumers on change (used by SidebarShell to render the injected
//     nav and respond to collapse).
//   - SidebarActionsContext — setSidebarContent + toggleCollapsed.
//     Referentially stable, so consumers that only need to push content
//     (e.g. CollectionsLayout via useSidebarContent) don't re-render
//     when state changes — preventing an infinite render loop where
//     `useSidebarContent(<CollectionsNav />)` would otherwise re-run its
//     effect on every render because JSX is a fresh ref each time.
//
// The public hook `useSidebar()` exposes the combined shape promised by
// the brief; `useSidebarContent(content)` only subscribes to actions,
// and runs `setSidebarContent` in a useEffect with `content` as the
// dependency, clearing on unmount.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const COLLAPSE_KEY = 'tricivo:sidebar:collapsed';

export interface SidebarContextValue {
  sidebarContent: ReactNode;
  setSidebarContent: (content: ReactNode) => void;
  isCollapsed: boolean;
  toggleCollapsed: () => void;
}

interface SidebarState {
  sidebarContent: ReactNode;
  isCollapsed: boolean;
}

interface SidebarActions {
  setSidebarContent: (content: ReactNode) => void;
  toggleCollapsed: () => void;
}

const SidebarStateContext = createContext<SidebarState | null>(null);
const SidebarActionsContext = createContext<SidebarActions | null>(null);

function readPersisted(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

function writePersisted(collapsed: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  } catch {
    // localStorage unavailable (Safari private mode, server) — ignore.
  }
}

interface SidebarProviderProps {
  children: ReactNode;
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  const [sidebarContent, setSidebarContent] = useState<ReactNode>(null);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => readPersisted());

  useEffect(() => {
    writePersisted(isCollapsed);
  }, [isCollapsed]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((v) => !v);
  }, []);

  // Actions are stable across state changes — the useState setter is
  // referentially stable, and toggleCollapsed is memoised. Components
  // that only need to push content (CollectionsLayout etc.) subscribe
  // to this context and do NOT re-render when sidebarContent flips.
  const actions = useMemo<SidebarActions>(
    () => ({ setSidebarContent, toggleCollapsed }),
    [toggleCollapsed],
  );

  // State context value is memoised so that re-renders of the provider
  // for unrelated reasons don't churn consumers.
  const state = useMemo<SidebarState>(
    () => ({ sidebarContent, isCollapsed }),
    [sidebarContent, isCollapsed],
  );

  return (
    <SidebarActionsContext.Provider value={actions}>
      <SidebarStateContext.Provider value={state}>
        {children}
      </SidebarStateContext.Provider>
    </SidebarActionsContext.Provider>
  );
}

export function useSidebar(): SidebarContextValue {
  const state = useContext(SidebarStateContext);
  const actions = useContext(SidebarActionsContext);
  if (state === null || actions === null) {
    throw new Error('useSidebar must be used inside SidebarProvider');
  }
  return {
    sidebarContent: state.sidebarContent,
    isCollapsed: state.isCollapsed,
    setSidebarContent: actions.setSidebarContent,
    toggleCollapsed: actions.toggleCollapsed,
  };
}

/**
 * Push the provided React node into the sidebar content slot for the
 * lifetime of the calling component. Clears the slot on unmount so a
 * stale module nav never lingers when its layout unmounts without a
 * replacement on the next route.
 *
 * Subscribes only to the (stable) actions context, so callers can pass
 * inline JSX without triggering an effect-loop.
 */
export function useSidebarContent(content: ReactNode): void {
  const actions = useContext(SidebarActionsContext);
  if (actions === null) {
    throw new Error('useSidebarContent must be used inside SidebarProvider');
  }
  const { setSidebarContent } = actions;
  useEffect(() => {
    setSidebarContent(content);
    return () => {
      setSidebarContent(null);
    };
  }, [content, setSidebarContent]);
}
